import { isViewShaped } from './viewStorage.js';

/**
 * The "Is Collection" field — ONE name for one idea, at every layer.
 *
 *   canonical field key   `is_collection`          (public.fields, primary_space_id IS NULL)
 *   display name          "Is Collection"
 *   view configs persist  'is_collection'          (a filter's `field` / `columnId`)
 *   read it with          collectionFlag(node)
 *   guard a field ref     isCollectionField(ref)
 *
 * If you are adding a surface, use those names verbatim.
 *
 * ── What the value is ──────────────────────────────────────────────────────
 *
 * True when the item opens as a collection/view page rather than as a plain item.
 * The canonical row's own description calls it "generated from collection_source —
 * never written directly. Filterable flag", and this module is the flag finally
 * being wired to the one surface that word promises.
 *
 * The value is DERIVED, on both sides of the wire, and deliberately not read off
 * `items.is_collection` here. That column is a Postgres generated column, and
 * `itemRowToRecord` (src/data/itemFieldMapping.js) does not carry it onto the
 * record at all — what it carries instead is the self-referential `Collection`
 * link it synthesizes from `is_collection` OR a non-empty `collection_source`.
 * So `isViewShaped` is the value: it is the test every other collection-aware
 * surface in the app already uses (the View sidebar tab, nav entries, "Edit
 * collection", ItemHeader, ItemActionsMenu), it reads the same record shape the
 * browser and the server both build, and it needs no new column in
 * serverCollection's ITEM_COLUMNS. Reading a raw `is_collection` property would
 * be a second opinion about the same question, and it would be undefined on every
 * node the app actually filters over.
 *
 * ── Filter only, and it must stay that way ────────────────────────────────
 *
 * Decided 2026-09-07. This field is offered to the FILTER pickers and nowhere
 * else:
 *
 *   • NOT on the Edit tab. It is generated from `collection_source` and never
 *     written directly, so an editable control over it would be a lie. It IS
 *     written indirectly, by the item's `...` menu (ItemActionsMenu nulls
 *     `collection_source`), and that menu stays the only way to change it.
 *   • NOT a row on the Info tab.
 *   • NOT in ALL_INTERNAL_FIELDS / getPickableInternalFields, for exactly the
 *     reasons utils/createdDate.js gives for keeping `created` out of that set:
 *     it also feeds field CREATION (SetupWizard, SettingsModal), the type-schema
 *     editor, the Grid's column builder and the sort dropdown — none of which
 *     should see a field with no writable column behind it.
 *   • NOT reached by widening the catalog floor. The alternative was to stop
 *     excluding the whole `identity` field_group, which would also have exposed
 *     `id`, `primary_space_id`, `created_at` and `updated_at`.
 *
 * `readOnly` + the `checkbox` type carry that intent to any picker that asks.
 */

/** The identity: the canonical `fields` key, and what a stored filter persists. */
export const IS_COLLECTION_FIELD_KEY = 'is_collection';

/** The display name, matching the canonical row's display_name. */
export const IS_COLLECTION_FIELD_LABEL = 'Is Collection';

/**
 * Picker-shaped descriptor. `id`/`name` are the identity and `label` is what people
 * read — the same split every custom field uses, so FieldListPicker renders it
 * correctly without special-casing.
 *
 * `checkbox` is the type because the canonical row is `value_type: 'bool'` with
 * `field_widget: 'checkbox'`, and because it is what hands the chip the operator
 * pair this field exists for: is_checked ("show me only collections") and
 * is_not_checked ("hide collections"). See OPERATORS_BY_TYPE in predicates.js.
 *
 * No `field_status`, which fieldLifecycle counts as live — correct here, and the
 * same shape every other synthetic def carries (isFieldOfferable's contract is
 * that a field-shaped object with no lifecycle column is live).
 */
export const IS_COLLECTION_FIELD_DEF = Object.freeze({
  id: IS_COLLECTION_FIELD_KEY,
  name: IS_COLLECTION_FIELD_KEY,
  label: IS_COLLECTION_FIELD_LABEL,
  type: 'checkbox',
  source: 'internal',
  synthetic: true,
  readOnly: true,
});

/**
 * Column-shaped descriptor for the ONE control that speaks columns rather than fields: the
 * Grid/List inline filter row (GridCanvas/FilterBar.jsx). That row builds its dropdown from the
 * canvas's `columns` array, and this field deliberately has no column — so without an entry here
 * a filter authored in the toolbar resolves to nothing there, and a `<select>` whose value matches
 * none of its options displays the FIRST one. The row would calmly say "Title" while narrowing by
 * this field, and its operator list would be empty. That is the exact failure `columnForFilter`
 * and `filterFieldForColumn` were extracted to end (PR #1228); this is the residual case of it,
 * for the one filterable thing that is not a column.
 *
 * NOT A GRID COLUMN, and it must never be pushed into one. There is no Is Collection column by
 * decision — a column is a visible surface, and this field is filter-only. `accessor` is here
 * because anything column-shaped is expected to have one, not because the Grid renders it.
 */
export const IS_COLLECTION_FILTER_COLUMN = Object.freeze({
  id: IS_COLLECTION_FIELD_KEY,
  label: IS_COLLECTION_FIELD_LABEL,
  type: 'checkbox',
  readOnly: true,
  accessor: (row) => collectionFlag(row),
});

/**
 * True when a persisted field reference points at the Is Collection field.
 *
 * Matches the key ONLY, for the same reason isCreatedField does: a space can have
 * a jsonb field of its own called "Is Collection", and hijacking that would be
 * worse than the problem this solves.
 *
 * @param {string|null|undefined} fieldRef  `field` / `columnId` from a stored filter
 */
export function isCollectionField(fieldRef) {
  return fieldRef === IS_COLLECTION_FIELD_KEY;
}

/**
 * The node's Is Collection value, as a real boolean so `is_checked` /
 * `is_not_checked` compare cleanly rather than against undefined.
 *
 * @param {object} node  a transformed item record / graph node (needs `_rawFields`)
 * @returns {boolean}
 */
export function collectionFlag(node) {
  return !!isViewShaped(node);
}
